import React, { useState, useEffect } from 'react';
import {
  Target,
  Trophy,
  FileText,
  MessageCircle,
  CheckCircle,
  XCircle,
  ArrowRight,
  ArrowLeft,
  Star,
  Heart,
  Flag,
  Tag,
  Bookmark,
  Bell,
  Calendar,
  Clock,
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Users,
  User,
  Building,
  Briefcase,
  ShoppingCart,
  Package,
  Truck,
  CreditCard,
  Receipt,
  FileCheck,
  FileX,
  Folder,
  FolderOpen,
  Database,
  Server,
  Cloud,
  Zap,
  Rocket,
  Plane,
  Ship,
  Car,
  Bike,
  MapPin,
  Globe,
  Map,
  Compass,
  Navigation,
  Search,
  Filter,
  Settings,
  Cog,
  Wrench,
  Hammer,
  Key,
  Lock,
  Unlock,
  Shield,
  ShieldCheck,
  ShieldAlert,
  AlertCircle,
  AlertTriangle,
  Info,
  HelpCircle,
  Check,
  X,
  Plus,
  Minus,
  Edit,
  Trash2,
  Copy,
  Share,
  Download,
  Upload,
  Save,
  RefreshCw,
  RotateCw,
  Repeat,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  FastForward,
  Rewind,
  Volume2,
  VolumeX,
  Mic,
  Video,
  Camera,
  Image,
  Film,
  Music,
  Headphones,
  Radio,
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Mail,
  Send,
  Inbox,
  Archive,
  Eye,
  EyeOff,
  Maximize,
  Minimize,
  Move,
  GripVertical,
  GripHorizontal,
  LayoutGrid,
  LayoutList,
  Columns,
  Rows,
  Grid,
  List,
  Menu,
  MoreVertical,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Circle,
  Square,
  Triangle,
  Hexagon,
  Octagon,
  Diamond
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const StageManager = ({ config, availableIcons, onChange }) => {
  const [editingStage, setEditingStage] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const stages = config.stages || [];

  const handleAddStage = () => {
    const newStage = {
      id: `stage_${Date.now()}`,
      displayName: 'New Stage',
      displayNameTH: 'ขั้นตอนใหม่',
      icon: 'Target',
      headerClass: 'from-slate-500/90 to-slate-400/80 text-white border-slate-400/60 dark:from-slate-700 dark:to-slate-600 dark:text-slate-100 dark:border-slate-700/60',
      laneBg: 'bg-gradient-to-b from-slate-50/60 to-white dark:from-slate-800 dark:to-slate-900',
      order: stages.length + 1,
      enabled: true
    };
    setEditingStage(newStage);
  };

  const handleEditStage = (stage) => {
    setEditingStage({ ...stage });
  };

  const handleSaveStage = () => {
    if (!editingStage.id || !editingStage.displayName) {
      toast.error('Stage ID and display name are required');
      return;
    }

    const updatedStages = [...stages];
    const existingIndex = updatedStages.findIndex(s => s.id === editingStage.id);

    if (existingIndex >= 0) {
      updatedStages[existingIndex] = editingStage;
    } else {
      updatedStages.push(editingStage);
    }

    // Reorder stages
    updatedStages.forEach((stage, index) => {
      stage.order = index + 1;
    });

    onChange({
      ...config,
      stages: updatedStages
    });

    setEditingStage(null);
    toast.success('Stage saved successfully');
  };

  const handleDeleteStage = (stageId) => {
    // Check if stage is referenced in mappings
    const mapping = config.opportunityStageMapping || {};
    if (mapping[stageId]) {
      toast.error(`Cannot delete stage "${stageId}" because it is referenced in opportunity stage mappings`);
      return;
    }

    if (window.confirm(`Are you sure you want to delete stage "${stageId}"?`)) {
      const updatedStages = stages.filter(s => s.id !== stageId);
      
      // Reorder remaining stages
      updatedStages.forEach((stage, index) => {
        stage.order = index + 1;
      });

      onChange({
        ...config,
        stages: updatedStages
      });

      toast.success('Stage deleted successfully');
    }
  };

  const handleToggleEnabled = (stageId) => {
    const updatedStages = stages.map(stage => {
      if (stage.id === stageId) {
        return { ...stage, enabled: stage.enabled === false ? true : false };
      }
      return stage;
    });

    onChange({
      ...config,
      stages: updatedStages
    });
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updatedStages = [...stages];
    const draggedStage = updatedStages[draggedIndex];
    updatedStages.splice(draggedIndex, 1);
    updatedStages.splice(index, 0, draggedStage);

    // Reorder stages
    updatedStages.forEach((stage, idx) => {
      stage.order = idx + 1;
    });

    onChange({
      ...config,
      stages: updatedStages
    });

    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Icon mapping for display
  const iconMap = {
    Target, Trophy, FileText, MessageCircle, CheckCircle, XCircle,
    ArrowRight, ArrowLeft, Star, Heart, Flag, Tag, Bookmark, Bell,
    Calendar, Clock, DollarSign, TrendingUp, TrendingDown, BarChart3,
    PieChart, Users, User, Building, Briefcase, ShoppingCart, Package,
    Truck, CreditCard, Receipt, FileCheck, FileX, Folder, FolderOpen,
    Database, Server, Cloud, Zap, Rocket,
    Plane, Ship, Car, Bike, MapPin, Globe, Map, Compass, Navigation,
    Search, Filter, Settings, Cog, Wrench, Hammer, Key, Lock,
    Unlock, Shield, ShieldCheck, ShieldAlert, AlertCircle, AlertTriangle,
    Info, HelpCircle, Check, X, Plus, Minus, Edit, Trash2, Copy, Share,
    Download, Upload, Save, RefreshCw, RotateCw, Repeat, Play, Pause,
    SkipForward, SkipBack, FastForward, Rewind, Volume2, VolumeX,
    Mic, Video, Camera, Image, Film, Music, Headphones, Radio, Phone,
    PhoneCall, PhoneIncoming, PhoneOutgoing, PhoneMissed, Mail, Send,
    Inbox, Archive, Eye, EyeOff, Maximize, Minimize, Move, GripVertical,
    GripHorizontal, LayoutGrid, LayoutList, Columns, Rows, Grid, List,
    Menu, MoreVertical, MoreHorizontal, ChevronDown, ChevronUp,
    ChevronLeft, ChevronRight, Circle, Square, Triangle, Hexagon,
    Octagon, Diamond
  };

  const getIconComponent = (iconName) => {
    return iconMap[iconName] || Target;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          Pipeline Stages
        </h2>
        <button
          onClick={handleAddStage}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Stage
        </button>
      </div>

      {/* Stages List */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="divide-y divide-slate-200 dark:divide-slate-700">
          {stages.map((stage, index) => {
            const Icon = getIconComponent(stage.icon);
            return (
              <div
                key={stage.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors flex items-center gap-4 ${
                  draggedIndex === index ? 'opacity-50' : ''
                }`}
              >
                <GripVertical className="h-5 w-5 text-slate-400 cursor-move" />
                <Icon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      {stage.displayName}
                    </span>
                    {stage.displayNameTH && (
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        ({stage.displayNameTH})
                      </span>
                    )}
                    {stage.enabled === false && (
                      <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded">
                        Disabled
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    ID: {stage.id} | Order: {stage.order}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleEnabled(stage.id)}
                    className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    title={stage.enabled === false ? 'Enable' : 'Disable'}
                  >
                    {stage.enabled === false ? (
                      <EyeOff className="h-4 w-4 text-slate-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-slate-600" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEditStage(stage)}
                    className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                  </button>
                  <button
                    onClick={() => handleDeleteStage(stage.id)}
                    className="p-2 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Modal */}
      {editingStage && (
        <StageEditModal
          stage={editingStage}
          availableIcons={availableIcons}
          iconMap={iconMap}
          getIconComponent={getIconComponent}
          onSave={handleSaveStage}
          onCancel={() => setEditingStage(null)}
          onChange={(updatedStage) => setEditingStage(updatedStage)}
        />
      )}
    </div>
  );
};

const StageEditModal = ({ stage, availableIcons, iconMap, getIconComponent, onSave, onCancel, onChange }) => {
  const [localStage, setLocalStage] = useState(stage);

  useEffect(() => {
    setLocalStage(stage);
  }, [stage]);

  const handleChange = (field, value) => {
    const updated = { ...localStage, [field]: value };
    setLocalStage(updated);
    onChange(updated);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {stage.id ? 'Edit Stage' : 'Add Stage'}
            </h3>
            <button
              onClick={onCancel}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Stage ID *
              </label>
              <input
                type="text"
                value={localStage.id || ''}
                onChange={(e) => handleChange('id', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                placeholder="e.g., lead, opportunities"
                disabled={!!stage.id}
              />
              <p className="text-xs text-slate-500 mt-1">
                Lowercase, alphanumeric, underscores only. Cannot be changed after creation.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Display Name (EN) *
                </label>
                <input
                  type="text"
                  value={localStage.displayName || ''}
                  onChange={(e) => handleChange('displayName', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  placeholder="e.g., Lead"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Display Name (TH)
                </label>
                <input
                  type="text"
                  value={localStage.displayNameTH || ''}
                  onChange={(e) => handleChange('displayNameTH', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  placeholder="e.g., ลีด"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Icon *
              </label>
              <div className="grid grid-cols-8 gap-2 max-h-48 overflow-y-auto p-2 border border-slate-300 dark:border-slate-600 rounded-lg">
                {availableIcons.map((iconName) => {
                  const Icon = getIconComponent(iconName);
                  return (
                    <button
                      key={iconName}
                      onClick={() => handleChange('icon', iconName)}
                      className={`p-2 rounded-lg border transition-colors ${
                        localStage.icon === iconName
                          ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                      title={iconName}
                    >
                      <Icon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Header Classes (Tailwind CSS)
              </label>
              <input
                type="text"
                value={localStage.headerClass || ''}
                onChange={(e) => handleChange('headerClass', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono text-sm"
                placeholder="from-indigo-500/90 to-indigo-400/80 text-white..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Lane Background Classes (Tailwind CSS)
              </label>
              <input
                type="text"
                value={localStage.laneBg || ''}
                onChange={(e) => handleChange('laneBg', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono text-sm"
                placeholder="bg-gradient-to-b from-indigo-50/60 to-white..."
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enabled"
                checked={localStage.enabled !== false}
                onChange={(e) => handleChange('enabled', e.target.checked)}
                className="w-4 h-4 text-indigo-600 rounded"
              />
              <label htmlFor="enabled" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Enabled
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StageManager;

